import { EventStatus } from '../constants/eventStatus';
import { Ticket } from '../models/tickets';
import { CheckoutState } from '../state';
import { getScreenWidth, getUserAgent } from '../utils/browser';
import {
    hasRemainingQuantityDisplayEnabled,
    hasSomeTicketWithMoreThanOneVariant,
} from '../utils/tickets';
import {
    getCorrelationId,
    getIsOrganizationExcludedFromPostCheckoutMandatoryQuestions,
    getIsRegEvent,
    getSessionId,
    hasFewTicketsLeftUrgencySignal,
    hasSalesEndSoonUrgencySignal,
    isAndroidApp,
    isEventbriteTLD,
    isIosApp,
} from './app';
import { getIsApplePaySupported } from './applePay';
import {
    getCurrency,
    getEventId,
    getEventOrganizationId,
    getEventStatus,
    getHasPromoCode,
    getIsEventOnline,
    getIsFreeEvent,
    getIsReservedSeating,
    getPublishedDateInEpoch,
    getSurveyType,
    getTicketReseller,
    getWaitlistSettings,
    hasSpecialInstructions,
    isDonation,
    isListedEvent,
    isLockedEvent,
    isSalesClient,
    isSeries,
    isSeriesParent,
} from './event';
import {
    getHasRequiredQuestions,
    hasQuestions,
    isApplePayAvailableAsExpressPaymentMethod,
    isGooglePayAvailableAsExpressPaymentMethod,
    isPaypalAvailableAsExpressPaymentMethod,
} from './expressCheckoutConditions';
import { hasOfflinePaymentMethods } from './order/typedOrderSelectors';
import { getSurvey } from './survey';
import {
    getHasAddonsTicketsAvailable,
    getHasDonationTicketsAvailable,
} from './tickets';
import { getTicketsById } from './tickets/pureSelectors';
import { getUserId, isAuthenticated, isUserLoggedIn } from './user';

export const getStatsigConfigFromState = (state: CheckoutState) => {
    const userAgent = getUserAgent() ?? {};
    const tickets: Ticket[] = Object.values(getTicketsById(state));
    const isFree = !!getIsFreeEvent(state);
    const isLoggedIn = isAuthenticated(state) || isUserLoggedIn(state);

    const {
        hasRequiredCannedQuestions,
        hasRequiredCustomQuestions,
        hasTaxQuestions,
        hasOptionalQuestions,
        hasInventoryQuestions,
        hasWaiverQuestions,
    } = getSurvey(state);

    return {
        custom: {
            browserAndDeviceSupportApplePay: getIsApplePaySupported(state),
            eventId: getEventId(state),

            ...(userAgent
                ? { userAgentLowerCase: userAgent.toLowerCase() }
                : {}),
            hasAddonsAvailable: getHasAddonsTicketsAvailable(state),
            hasDonationTicketsAvailable: getHasDonationTicketsAvailable(state),
            hasFewTicketsLeftUrgencySignal:
                hasFewTicketsLeftUrgencySignal(state),
            hasInventoryQuestions,
            hasMoreThanOneTicketType: tickets.length > 1,
            hasMultivariantTickets:
                hasSomeTicketWithMoreThanOneVariant(tickets),
            hasOfflinePaymentMethods: hasOfflinePaymentMethods(state),
            hasOptionalQuestions,
            hasPrepaymentQuestions: getHasRequiredQuestions(state),
            hasPromoCode: getHasPromoCode(state),
            hasQuestions: hasQuestions(state),
            hasRemainingQuantityDisplay:
                hasRemainingQuantityDisplayEnabled(tickets),
            hasRequiredCannedQuestions,
            hasRequiredCustomQuestions,
            hasSalesEndSoonUrgencySignal: hasSalesEndSoonUrgencySignal(state),
            hasScreenWidth: getScreenWidth(),
            hasSpecialInstructions: hasSpecialInstructions(state),
            hasTaxQuestions,
            hasTicketResellers: !!getTicketReseller(state),
            hasWaiverQuestions,
            isApplePayEPPAvailable:
                isApplePayAvailableAsExpressPaymentMethod(state),
            isDonation: isDonation(state),
            isEventbriteTLD: isEventbriteTLD(state),
            isFree: isFree,
            isGooglePayEPPAvailable:
                isGooglePayAvailableAsExpressPaymentMethod(state),
            isLive: getEventStatus(state) === EventStatus.live,
            isLocked: isLockedEvent(state),
            isOrganizationExcludedFromPostCheckoutMandatoryQuestions:
                getIsOrganizationExcludedFromPostCheckoutMandatoryQuestions(
                    state,
                ),
            isPaid: !isFree,
            isPaypalEPPAvailable:
                isPaypalAvailableAsExpressPaymentMethod(state),
            isRegistrationEvent: !!getIsRegEvent(state),
            isReservedSeating: getIsReservedSeating(state),
            isSalesClient: isSalesClient(state),
            isSeries: isSeries(state),
            isSeriesParent: isSeriesParent(state),
            isUserLoggedIn: isLoggedIn,
            isWaitlist: !!getWaitlistSettings(state),
            listed: isListedEvent(state),
            onlineEvent: getIsEventOnline(state),
            surveyType: getSurveyType(state),
            useAndroidProfile: isAndroidApp(state),
            useIosProfile: isIosApp(state),
            currency: getCurrency(state),
            publishedDateInEpoch: getPublishedDateInEpoch(state),
        },
        customIDs: {
            correlationId: getCorrelationId(state),
            eventId: getEventId(state),
            organizerId: getEventOrganizationId(state),
            sessionId: getSessionId(state),
        },
        userID: getUserId(state),
    };
};
